import axios from 'axios';
import {
    USER_REGISTER_REQUEST,
    USER_REGISTER_FAIL,
    USER_REGISTER_SUCCESS,
    USER_LOGIN_REQUEST,
    USER_LOGIN_SUCCESS,
    USER_LOGIN_FAIL,
    USER_LOGOUT,
    USER_DELETE_REQUEST,
    USER_DELETE_SUCCESS,
    USER_DELETE_FAIL,
    USER_RESET_PASSWORD_SUCCESS,
    USER_RESET_PASSWORD_REQUEST,
    USER_RESET_PASSWORD_FAIL,
    USER_UPDATE_REQUEST,
    USER_UPDATE_FAIL,
    USER_UPDATE_USER_SUCCESS,
    ADMIN_USERS_LIST_REQUEST,
    ADMIN_USERS_LIST_SUCCESS,
    ADMIN_USERS_LIST_FAIL,
    ADMIN_USERS_LIST_RESET
} from '../constants/userConstants';

export const register = (username, email, password) => async(dispatch) => {
    try {
        dispatch({type: USER_REGISTER_REQUEST});

        const config = {
            headers: {
                "Content-type": "application/json"
            }
        }

        const { data } = await axios.post('/api/users/register', {username, email, password}, config);

        dispatch({type: USER_REGISTER_SUCCESS, payload: data});

        localStorage.setItem('userInfo', JSON.stringify(data));
    } catch (error) {
        dispatch({
            type: USER_REGISTER_FAIL,
            payload: error.response && error.response.data.message ? error.response.data.message : error.message
        });
    }
}

export const login = (username, password) => async(dispatch) => {
    try {
        dispatch({type: USER_LOGIN_REQUEST});

        const config = {
            headers: {
                "Content-type": "application/json"
            }
        }

        const { data } = await axios.post('/api/users/login', {username, password}, config);

        dispatch({ type: USER_LOGIN_SUCCESS, payload: data});

        localStorage.setItem('userInfo', JSON.stringify(data));
    } catch (error) {
        dispatch({
            type: USER_LOGIN_FAIL,
            payload: error.response && error.response.data.message ? error.response.data.message : error.message,
        });
    }
}

export const logout = () => async(dispatch) => {
    localStorage.removeItem('userInfo');
    dispatch({ type: USER_LOGOUT });
}

// Re-read isAdmin and role from the server. They are stored at login, so without this a change to
// either (including one made straight in the database) never reaches an open session, and the UI
// keeps offering pages the server refuses.
export const refreshUser = () => async(dispatch, getState) => {
    const { userLogin: { userInfo } } = getState();

    if(!userInfo?.token) {
        return;
    }

    try {
        const config = {
            headers: {
                Authorization: `Bearer ${userInfo.token}`
            }
        }

        const { data } = await axios.get('/api/users/me', config);

        // Merged, not replaced: the token and anything else stored at login stay put
        const merged = { ...userInfo, ...data };

        dispatch({ type: USER_LOGIN_SUCCESS, payload: merged });
        localStorage.setItem('userInfo', JSON.stringify(merged));
    } catch (error) {
        // A rejected token means the session is over; anything else (offline, server down) is not a
        // reason to throw someone out
        if(error.response?.status === 401) {
            localStorage.removeItem('userInfo');
            dispatch({ type: USER_LOGOUT });
        }
    }
}

export const resetPassword = (newPassword, confirmNewPassword) => async(dispatch, getState) => {
    try {
        dispatch({type: USER_RESET_PASSWORD_REQUEST});

        const { userLogin: { userInfo }} = getState();

        const config = {
            headers: {
                Authorization: `Bearer ${userInfo.token}`
            }
        }

        const { data } = await axios.post('/api/users/resetpassword', {newPassword, confirmNewPassword}, config);

        dispatch({ type: USER_RESET_PASSWORD_SUCCESS, payload: data});
    } catch (error) {
        dispatch({
            type: USER_RESET_PASSWORD_FAIL,
            payload: error.response && error.response.data.message ? error.response.data.message : error.message,
        });
    }
}

export const resetPasswordWithToken = (email, token, newPassword, confirmNewPassword) => async(dispatch, getState) => {
    try {
        dispatch({type: USER_RESET_PASSWORD_REQUEST});

        const { data } = await axios.post('/api/users/forgot/resetpassword', {email, token, newPassword, confirmNewPassword});

        if(data.success) {
            dispatch({ type: USER_RESET_PASSWORD_SUCCESS, payload: data});
        } else {
            throw new Error(data.message);
        }
    } catch (error) {
        dispatch({
            type: USER_RESET_PASSWORD_FAIL,
            payload: error.response && error.response.data.message ? error.response.data.message : error.message,
        });
    }
}

export const deleteAcc = (_id) => async(dispatch, getState) => {
    try {
        dispatch({type: USER_DELETE_REQUEST});

        const { userLogin: { userInfo }} = getState();

        const config = {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${userInfo.token}`
            }
        }

        const { data } = await axios.delete(`/api/users/delete/${_id}`, config);

        dispatch({type: USER_DELETE_SUCCESS, payload: data});

        logout();
    } catch (error) {
        dispatch({
            type: USER_DELETE_FAIL,
            payload: error.response && error.response.data.message ? error.response.data.message : error.message
        })
    }
}

export const postAddress = (streetAddress, cityAddress, stateAddress, zipAddress) => async(dispatch, getState) => {
    try {
        dispatch({type: USER_UPDATE_REQUEST});

        const { userLogin: { userInfo }} = getState();

        const config = {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${userInfo.token}`
            }
        }

        const { data } = await axios.post(`/api/users/update/address`, {streetAddress, cityAddress, stateAddress, zipAddress}, config);

        dispatch({ type: USER_UPDATE_USER_SUCCESS, payload: data});

        localStorage.setItem('userInfo', JSON.stringify(data));

        logout();
    } catch (error) {
        dispatch({
            type: USER_UPDATE_FAIL,
            payload: error.response && error.response.data.message ? error.response.data.message : error.message
        })
    }
}

export const deleteAddress = () => async(dispatch, getState) => {
    try {
        dispatch({type: USER_UPDATE_REQUEST});

        const { userLogin: { userInfo }} = getState();

        const config = {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${userInfo.token}`
            }
        }

        const { data } = await axios.delete(`/api/users/update/address`, config);

        dispatch({ type: USER_UPDATE_USER_SUCCESS, payload: data});

        localStorage.setItem('userInfo', JSON.stringify(data));

        logout();
    } catch (error) {
        dispatch({
            type: USER_UPDATE_FAIL,
            payload: error.response && error.response.data.message ? error.response.data.message : error.message
        })
    }
}

export const updateEmail = (newEmail) => async(dispatch, getState) => {
    try {
        dispatch({type: USER_UPDATE_REQUEST});

        const {userLogin: {userInfo}} = getState();

        const config = {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${userInfo.token}`
            }
        }

        const {data} = await axios.put(`/api/users/update/email`, {newEmail}, config);

        dispatch({type: USER_UPDATE_USER_SUCCESS, payload: data});

        // Update local storage with new user info
        localStorage.setItem('userInfo', JSON.stringify(data));

    } catch (error) {
        dispatch({
            type: USER_UPDATE_FAIL,
            payload: error.response && error.response.data.message ? error.response.data.message : error.message
        });
    }
}

// `background: true` fetches silently — no spinner, and a failure leaves the current list alone.
export const listUsers = ({ background = false } = {}) => async (dispatch, getState) => {
    try {
        if (!background) {
            dispatch({
                type: ADMIN_USERS_LIST_REQUEST,
            });
        }

        // Get user info from state
        const {
            userLogin: { userInfo },
        } = getState();

        // Configure headers with token
        const config = {
            headers: {
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        // Make API call
        const { data } = await axios.get('/api/users/admin/getall', config);

        dispatch({
            type: ADMIN_USERS_LIST_SUCCESS,
            payload: data,
        });
    } catch (error) {
        if (background) return;
        dispatch({
            type: ADMIN_USERS_LIST_FAIL,
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
    }
};

export const resetUserList = () => (dispatch) => {
    dispatch({ type: ADMIN_USERS_LIST_RESET });
};
